/**
 * trackWrite — report whether a write actually reached the cloud, including
 * when it neither succeeds nor fails but simply never answers.
 *
 * Walter 2026-10-09. His Chrome runs Ghostery, and his console showed
 * `net::ERR_BLOCKED_BY_CLIENT` on Firestore requests. His call on fixing it:
 * *"I'm going to hold off on that and just see how bad an impact Ghostery
 * has, as the spec tells users won't turn it off either"* — so the app has to
 * cope with a blocked database rather than ask people to unblock it.
 *
 * LIST saves already detect this: they go through a TRANSACTION, which needs
 * the server and rejects when blocked, and `saveHealth` counts the rejections
 * (three in three minutes raises the banner). REMINDER writes are plain
 * `addDoc` / `updateDoc`: their promise settles when the SERVER acknowledges,
 * so a blocked one does not reject — it hangs, forever, while the SDK retries
 * in the background. A try/catch would wait with it, silently. That is why
 * his reminder edit vanished while the list text around it saved fine.
 *
 * So the signal is a DEADLINE, not an exception: a write that hasn't been
 * acknowledged within `timeoutMs` is reported as failing. It may still land
 * later — Firestore keeps retrying and holds the change locally meanwhile,
 * which is exactly what the banner says ("saved on this device but NOT
 * reaching the cloud").
 *
 * A slow phone network can trip the deadline too. That is tolerable by
 * design: the banner needs three failures inside three minutes AND no success
 * in between, and any acknowledged write clears the burst.
 */

export const DEFAULT_WRITE_TIMEOUT_MS = 10000;

/**
 * trackWrite(promise, { onFailure, onSuccess, timeoutMs, setTimeoutFn, clearTimeoutFn })
 *
 * Returns a promise that settles exactly as `promise` does — callers keep
 * their existing behaviour, including any rejection. The reporting is a side
 * effect:
 *   - acknowledged in time  → onSuccess()
 *   - rejected              → onFailure(), then the rejection is re-thrown
 *   - still unanswered at the deadline → onFailure() once; if it is
 *     acknowledged later, onSuccess() then clears the burst.
 */
export function trackWrite(promise, {
  onFailure,
  onSuccess,
  timeoutMs = DEFAULT_WRITE_TIMEOUT_MS,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
} = {}) {
  let settled = false;
  const timer = setTimeoutFn(() => {
    if (settled) return;
    try { onFailure?.(); } catch { /* reporting must never break a write */ }
  }, timeoutMs);

  const done = (fn) => {
    settled = true;
    try { clearTimeoutFn(timer); } catch { /* ignore */ }
    try { fn?.(); } catch { /* reporting must never break a write */ }
  };

  return Promise.resolve(promise).then(
    (value) => { done(onSuccess); return value; },
    (err) => { done(onFailure); throw err; },
  );
}

export default trackWrite;
